import React from "react";
import { useTranslation } from "react-i18next";
import { Box } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import DoneTwoTone from "@mui/icons-material/DoneTwoTone";
import CloseTwoTone from "@mui/icons-material/CloseTwoTone";
import DirectionsCarFilledOutlinedIcon from "@mui/icons-material/DirectionsCarFilledOutlined";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import AppButton from "../../UI/AppButtons";
import BookingGetters from "../../../store/onlineBooking/getters";
import { CancellationUpgradeProposal } from "../../../@types/DTO/UpgradeProposals";
import i18n from "../../../config/i18n";

export type ResolvedProtectionUpgradeFeature = {
  label: string;
  included: boolean;
};

export type ResolvedVehicleUpgradeSpec = {
  iconUrl: string;
  name: string;
};

export type ResolvedVehicleUpgrade = {
  groupCode: string;
  price: number;
  isDailyPrice?: boolean;
  vehicleName?: string;
  imageUrl?: string;
  specifications?: ResolvedVehicleUpgradeSpec[];
};

export type ResolvedProtectionUpgrade = {
  description: string;
  absolutePrice: number;
  priceDelta: number;
  features: ResolvedProtectionUpgradeFeature[];
};

type UpgradeCard = {
  key: "cancellation" | "vehicle" | "protection";
  icon: React.ElementType;
  title: string;
  copy: string;
  priceLabel?: string;
  features?: ResolvedProtectionUpgradeFeature[];
  imageUrl?: string;
  specifications?: ResolvedVehicleUpgradeSpec[];
  selected: boolean;
  onToggle: () => void;
};

type Props = {
  cancellationUpgrade?: CancellationUpgradeProposal;
  vehicleUpgrade?: ResolvedVehicleUpgrade;
  protectionUpgrade?: ResolvedProtectionUpgrade;
  selectedCancellation: boolean;
  selectedVehicleUpgrade: boolean;
  selectedProtection: boolean;
  onToggleCancellation: () => void;
  onToggleVehicleUpgrade: () => void;
  onToggleProtection: () => void;
  currencySymbol: string;
  onContinue: () => void;
  hideTitle?: boolean;
  hideFooterButtons?: boolean;
};

export default function UpgradeProposals({
  cancellationUpgrade,
  vehicleUpgrade,
  protectionUpgrade,
  selectedCancellation,
  selectedVehicleUpgrade,
  selectedProtection,
  onToggleCancellation,
  onToggleVehicleUpgrade,
  onToggleProtection,
  currencySymbol,
  onContinue,
  hideTitle = false,
  hideFooterButtons = false,
}: Props) {
  const { t: translate } = useTranslation();
  const appSettings = BookingGetters.getAppSettings();
  const dynamicTheme = useTheme();
  const accentColor =
    dynamicTheme?.palette["secondaryColor"]?.main || "#2e7d32";
  const textColor =
    dynamicTheme?.palette["backgroundColor"]?.contrastText || "#19344F";
  const mutedTextColor = dynamicTheme?.palette["gray"]?.main || "#9CA3AF";

  const locationLang = i18n.language;
  const textResourceDetails =
    BookingGetters.getTextResourceDetailsBasedOnLang();
  const textResourceData =
    textResourceDetails?.[locationLang]?.flatMap((item) => item.data || []) ||
    [];
  const cancellationUpgradeCopyText = textResourceData.find(
    (item) => item.key === "cancellationUpgradeCopyLabel"
  );
  const vehicleUpgradeCopyText = textResourceData.find(
    (item) => item.key === "vehicleUpgradeCopyLabel"
  );
  const protectionUpgradeCopyText = textResourceData.find(
    (item) => item.key === "protectionUpgradeCopyLabel"
  );

  const buildUpgradeCopy = (baseText: string, priceString: string): string =>
    baseText.includes("{{price}}")
      ? baseText.replace("{{price}}", priceString)
      : `${priceString} ${baseText}`;

  const cards: UpgradeCard[] = [];

  if (cancellationUpgrade?.enabled) {
    const cancellationPriceString = `${cancellationUpgrade.price.toFixed(
      2
    )} ${currencySymbol}`;
    cards.push({
      key: "cancellation",
      icon: ShieldOutlinedIcon,
      title:
        cancellationUpgrade.description || translate("Cancellation upgrade"),
      copy: buildUpgradeCopy(
        cancellationUpgradeCopyText?.body ||
          translate(
            "You can cancel your reservation up to 24 hours before the rental starts at no additional cost.\n\nFor only {{price}}, you can get Last Minute cancellation up to 1 hour before the rental starts.",
            { price: cancellationPriceString }
          ),
        cancellationPriceString
      ),
      priceLabel: cancellationPriceString,
      selected: selectedCancellation,
      onToggle: onToggleCancellation,
    });
  }
  if (vehicleUpgrade) {
    const vehicleUpgradePriceString = `${vehicleUpgrade.price.toFixed(
      2
    )} ${currencySymbol}${
      vehicleUpgrade.isDailyPrice ? translate("/day") : ""
    }`;
    cards.push({
      key: "vehicle",
      icon: DirectionsCarFilledOutlinedIcon,
      title:
        vehicleUpgrade.vehicleName ||
        `${translate("Group")} ${vehicleUpgrade.groupCode}`,
      copy:
        vehicleUpgrade.price > 0
          ? buildUpgradeCopy(
              vehicleUpgradeCopyText?.body ||
                translate("more and TRAVEL MORE COMFORTABLY"),
              vehicleUpgradePriceString
            )
          : translate("No extra cost"),
      imageUrl: vehicleUpgrade.imageUrl,
      specifications: vehicleUpgrade.specifications,
      selected: selectedVehicleUpgrade,
      onToggle: onToggleVehicleUpgrade,
    });
  }
  if (protectionUpgrade) {
    cards.push({
      key: "protection",
      icon: ShieldOutlinedIcon,
      title: protectionUpgrade.description,
      copy:
        protectionUpgrade.priceDelta !== 0
          ? buildUpgradeCopy(
              protectionUpgradeCopyText?.body ||
                translate("more per day and TRAVEL SAFER"),
              `${protectionUpgrade.priceDelta.toFixed(2)} ${currencySymbol}`
            )
          : translate("No extra cost"),
      features: protectionUpgrade.features,
      selected: selectedProtection,
      onToggle: onToggleProtection,
    });
  }

  if (!cards.length) return null;

  const cardWidth = "440px";

  return (
    <div className="h-full w-full">
      {!hideTitle && (
        <h2 className="text-black text-xl font-semibold mb-2 text-left">
          {translate("Upgrade offers")}
        </h2>
      )}
      <div
        className={`flex flex-wrap justify-center items-stretch ${
          hideTitle ? "pt-6" : ""
        }`}
        style={{ gap: "24px" }}
      >
        {cards.map((card) => {
          const Icon = card.icon;
          return (
            <div
              key={card.key}
              className="shadow-lg p-8 flex-1 flex flex-col justify-between rounded-3xl bg-white text-black relative"
              style={{
                flexBasis: cardWidth,
                maxWidth: cardWidth,
                minWidth: "260px",
                flexGrow: 0,
              }}
            >
              <div className="flex flex-col items-start">
                <div className="flex items-center gap-3">
                  <Box
                    sx={{
                      width: 44,
                      height: 44,
                      flexShrink: 0,
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      backgroundColor: alpha(accentColor, 0.12),
                    }}
                  >
                    <Icon sx={{ color: accentColor, fontSize: 24 }} />
                  </Box>
                  <h5
                    className="text-xl font-semibold text-left"
                    style={{ color: textColor }}
                  >
                    {card.title}
                  </h5>
                </div>
                {card.priceLabel && (
                  <p
                    className="text-lg mb-2 font-bold text-left"
                    style={{ color: textColor }}
                  >
                    {card.priceLabel}
                  </p>
                )}
                <p
                  className={
                    card.priceLabel
                      ? "text-md text-left mb-2"
                      : "text-sm text-left mb-2"
                  }
                  style={{
                    whiteSpace: "pre-line",
                    color: textColor,
                    paddingLeft: card.priceLabel ? undefined : "56px",
                  }}
                >
                  {card.copy}
                </p>

                {card.imageUrl && (
                  <Box
                    component="img"
                    src={card.imageUrl}
                    alt={card.title}
                    sx={{
                      width: "100%",
                      maxHeight: 140,
                      objectFit: "contain",
                      mb: 2,
                    }}
                  />
                )}
                {card.specifications && card.specifications.length > 0 && (
                  <div
                    className="w-full flex flex-nowrap justify-center gap-2 mb-2"
                    style={{ overflowX: "auto" }}
                  >
                    {card.specifications.map((spec) => (
                      <div
                        key={spec.name}
                        className="flex flex-col items-center flex-shrink-0"
                        style={{ minWidth: 64, maxWidth: 100 }}
                      >
                        <Box
                          component="img"
                          src={spec.iconUrl}
                          alt={spec.name}
                          sx={{ width: 24, height: 24 }}
                        />
                        <span
                          className="text-xs text-center"
                          style={{
                            color: textColor,
                            whiteSpace: "normal",
                            wordBreak: "break-word",
                            maxWidth: 100,
                          }}
                        >
                          {spec.name}
                        </span>
                      </div>
                    ))}
                  </div>
                )}

                {card.features && card.features.length > 0 && (
                  <div className="w-full mb-2">
                    {card.features.map((feature) => (
                      <div
                        key={feature.label}
                        className="flex items-center text-left mb-1"
                      >
                        {feature.included ? (
                          <DoneTwoTone
                            fontSize="small"
                            className="text-green-800 mr-2"
                          />
                        ) : (
                          <CloseTwoTone
                            fontSize="small"
                            className="text-gray-400 mr-2"
                          />
                        )}
                        <span
                          className="text-sm"
                          style={{
                            color: feature.included
                              ? textColor
                              : mutedTextColor,
                          }}
                        >
                          {feature.label}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <AppButton
                className="h-11 w-[200px] mx-auto cursor-pointer"
                color={card.selected ? "primary" : "secondaryColor"}
                onClick={card.onToggle}
                aria-pressed={card.selected}
                aria-label={`${
                  card.selected
                    ? translate("Remove upgrade")
                    : translate("Get upgrade")
                }: ${card.title}`}
                style={{
                  marginTop: "9px",
                  background: card.selected
                    ? dynamicTheme?.palette["secondaryColor"]?.main
                    : dynamicTheme?.palette["primary"].main,
                  borderRadius: `${appSettings?.buttonBorderRadius || 5}px`,
                }}
              >
                <p className="w-full text-center" style={{ color: "white" }}>
                  {card.selected
                    ? translate("Remove upgrade")
                    : translate("Get upgrade")}
                </p>
              </AppButton>
            </div>
          );
        })}
      </div>

      {!hideFooterButtons && (
        <div className="flex justify-center gap-4 mt-8 mb-8">
          <AppButton
            className="h-14 cursor-pointer"
            color="primary"
            onClick={onContinue}
            style={{
              background: dynamicTheme?.palette["primary"]?.main,
              borderRadius: `${appSettings?.buttonBorderRadius || 5}px`,
            }}
          >
            <p className="w-full text-center" style={{ color: "white" }}>
              {translate("Continue")}
            </p>
          </AppButton>
        </div>
      )}
    </div>
  );
}
